import Icon from "@/components/Icon"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Spinner } from "@/components/ui/spinner"
import { type CandidateShift, useCandidateVehicles } from "@/hooks/useCandidateVehicles"
import { useAssignVehicle, useUnassignVehicle } from "@/hooks/useVehicleAssignment"

const formatDistance = (meters: number | null, label: string) => {
  if (meters === null) return null
  const distance = meters < 1000 ? `${Math.round(meters)} m` : `${(meters / 1000).toFixed(1)} km`
  return `${distance} from ${label}`
}

interface AssignVehicleDialogProps {
  shift: CandidateShift
  currentVehicleReg: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function AssignVehicleDialog({
  shift,
  currentVehicleReg,
  open,
  onOpenChange,
}: AssignVehicleDialogProps) {
  const { data: vehicles, isLoading, error } = useCandidateVehicles(open ? shift : undefined)
  const assign = useAssignVehicle()
  const unassign = useUnassignVehicle()

  // Closes this dialog only: the block dialog behind it shows the vehicle that just changed.
  const close = () => onOpenChange(false)
  const busy = assign.isPending || unassign.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{currentVehicleReg ? "Change vehicle" : "Assign a vehicle"}</DialogTitle>
          <DialogDescription>
            {currentVehicleReg
              ? `This block currently has ${currentVehicleReg}. Vehicles below are eligible, nearest the driver's sticky location first, then nearest the depot.`
              : "Vehicles eligible for this block, nearest the driver's sticky location first, then nearest the depot."}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Spinner size="md" />
          </div>
        ) : error ? (
          <Alert variant="destructive">
            <Icon name="warningCircleFilled" className="h-4 w-4" />
            <AlertDescription>
              Error loading vehicles: {error instanceof Error ? error.message : "Unknown error"}
            </AlertDescription>
          </Alert>
        ) : (vehicles ?? []).length === 0 ? (
          <span className="py-6 text-center text-sm text-palette-jungleGreen500 italic">
            No eligible vehicles for this block.
          </span>
        ) : (
          <ScrollArea className="max-h-[50vh]">
            <div className="pr-3">
              {(vehicles ?? []).map((candidate) => (
                <div
                  key={candidate.id}
                  className="flex items-center justify-between gap-3 border-b border-palette-jungleGreen100 py-3 last:border-0"
                >
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-sm font-medium text-palette-jungleGreen900">
                      {candidate.registrationDisplay}
                    </span>
                    <span className="text-xs text-palette-jungleGreen700">
                      {candidate.vehicleGroup ?? "No group"}
                    </span>
                    {[
                      formatDistance(
                        candidate.stickyDistanceMeters,
                        `sticky location ${shift.stickyLocation}`,
                      ),
                      formatDistance(candidate.depotDistanceMeters, "depot"),
                    ]
                      .filter(Boolean)
                      .map((line) => (
                        <span key={line} className="text-xs text-palette-jungleGreen700">
                          {line}
                        </span>
                      ))}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="shrink-0"
                    disabled={busy}
                    onClick={() =>
                      assign.mutate(
                        {
                          shiftId: shift.id,
                          vehicleId: candidate.id,
                          registrationDisplay: candidate.registrationDisplay,
                        },
                        { onSuccess: close },
                      )
                    }
                  >
                    Assign
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  )
}
